import React, { useEffect, useRef } from 'react';
import { resolveTokenColor } from '@template-project/funnel-design-tokens';
import type { JsonObject, JsonValue } from '@trafilea/tss-lib-funnel-schema-ts';
import type { StepPageBlock } from '../types';
import {
  resolveResponsiveProps,
  useBreakpoint,
  type BlockResponsiveOverrides,
  type Breakpoint,
} from './responsive';
import { isFunnelUiEventsEnabled, useBlockEventEmitter } from '../events';

/** Reconcile backoffice snake_case keys with storefront camelCase. */
export function normalizeResponsiveOverrides(
  overrides: StepPageBlock['responsiveOverrides'] | undefined
): BlockResponsiveOverrides | undefined {
  if (!overrides || Array.isArray(overrides)) return undefined;
  const persistedOverrides = overrides as JsonObject;
  return {
    tablet: asJsonObject(persistedOverrides.tablet),
    mobileLandscape: asJsonObject(
      persistedOverrides.mobileLandscape ?? persistedOverrides.mobile_landscape
    ),
    mobilePortrait: asJsonObject(
      persistedOverrides.mobilePortrait ??
        persistedOverrides.mobile ??
        persistedOverrides.mobile_portrait
    ),
  };
}

function asJsonObject(value: JsonValue | undefined): JsonObject | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value : undefined;
}

export function mergeBlockStyles(block: StepPageBlock): React.CSSProperties {
  const style: React.CSSProperties = {};
  const styles = block.styles as JsonObject | undefined;
  const advanced = block.advanced as JsonObject | undefined;

  if (styles) {
    if (typeof styles.backgroundColor === 'string') {
      style.backgroundColor = resolveTokenColor(styles.backgroundColor);
    }
    if (typeof styles.color === 'string') {
      style.color = resolveTokenColor(styles.color);
    }
    if (typeof styles.padding === 'string') style.padding = styles.padding;
    if (typeof styles.margin === 'string') style.margin = styles.margin;
    if (typeof styles.borderRadius === 'string') style.borderRadius = styles.borderRadius;
  }
  if (advanced?.className && typeof advanced.className === 'string') {
    // className applied on wrapper via data attribute for host CSS hooks
  }
  if (block.layout) {
    const layout = block.layout;
    if (layout.order != null) style.order = layout.order;
  }
  return style;
}

export interface FidelityBlockWrapperProps {
  block: StepPageBlock;
  breakpoint?: Breakpoint;
  children: React.ReactElement;
}

export function FidelityBlockWrapper({ block, breakpoint, children }: FidelityBlockWrapperProps) {
  // ADR 0004 — onView emitter (IntersectionObserver, fires once per mount when
  // the block first enters the viewport). Declared before the (conditional)
  // useBreakpoint() call so these hooks keep a stable index across renders; held
  // in a ref so the observer effect stays stable while calling the latest
  // capability-gated emitter.
  //
  // SINGLE SOURCE OF onView (reconcile BL-37): this wrapper wraps every block, so
  // it is the one place onView is emitted. #609's parallel per-block
  // `useOnViewEmitter` was removed from ComponentRenderers in favor of this
  // universal path (it only covered 8 renderers, so keeping both double-emitted).
  // The observer uses the platform default threshold (0). Environments without
  // IntersectionObserver do not synthesize a server-rendered observation.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const blockEvents = useBlockEventEmitter(block);
  const emitViewRef = useRef<() => void>(() => {});
  const emittedBlockIdRef = useRef<string | null>(null);
  emitViewRef.current = () => blockEvents.onView();
  const hasOnViewBinding = Boolean(block.events?.onView?.length);
  useEffect(() => {
    if (!hasOnViewBinding || !isFunnelUiEventsEnabled()) return;
    if (emittedBlockIdRef.current === block.id) return;
    const el = wrapperRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    let active = true;
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (active && emittedBlockIdRef.current !== block.id && entry.isIntersecting) {
          emittedBlockIdRef.current = block.id;
          emitViewRef.current();
          observer.disconnect(); // onView fires once per mount
          break;
        }
      }
    });
    observer.observe(el);
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [hasOnViewBinding, block.id]);

  const activeBp = breakpoint ?? useBreakpoint();
  const mergedProps = resolveResponsiveProps(
    (block.props ?? {}) as JsonObject,
    normalizeResponsiveOverrides(block.responsiveOverrides),
    activeBp
  );
  const style = mergeBlockStyles({ ...block, props: mergedProps });
  const advanced = block.advanced as JsonObject | undefined;
  const className = typeof advanced?.className === 'string' ? advanced.className : undefined;

  return (
    <div
      ref={wrapperRef}
      data-block-id={block.id}
      data-block-type={block.type}
      className={className}
      style={Object.keys(style).length ? style : undefined}
    >
      {React.cloneElement(children, { block: { ...block, props: mergedProps } })}
    </div>
  );
}
